import { useRef, useState } from 'react'
import { FlatList, Pressable, ScrollView, Text, useWindowDimensions, View } from 'react-native'
import { useQuery } from '@apollo/client'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { SheetManager } from 'react-native-actions-sheet'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { basic } from '@/constants/colors'
import { formatLocalFiatAmount, marketPresentation } from '@/config/marketPresentation'
import { ASSET_OPTIONS, AssetOptions, PAGE_SIZE } from '@/constants/common'
import { GET_WALLET_BY_CURRENCY, USER_WALLET_EVENTS_CONNECTION } from '@/constants/graphql/queries'
import { WalletEventKind } from '@/types/wallet'
import { cn } from '@/utils/cn'
import { convertToDate } from '@/utils/formatter'
import { getBignumber, toCurrency } from '@/utils/numbers'
import { Button } from '@/components/base'
import BottomActionPanel from '@/components/BottomActionPanel'
import { ChevronRight, Deposit, ExpandMore, ReceiptOff, Spinner } from '@/components/icon'
import BrandLogo from '@/components/BrandLogo'
import { Stack } from '@/components/layout'
import NavigationBar from '@/components/NavigationBar'
import NotFoundScreen from '@/components/NotFoundScreen'
import RefreshControl from '@/components/RefreshControl'
import SectionHeader from '@/components/SectionHeader'

export default function CoinScreen() {
  const { t } = useTranslation()
  const router = useRouter()
  const { coin } = useLocalSearchParams<Record<string, string>>()

  const [selectedKind, setSelectedKind] = useState('all')

  const tabBarRef = useRef<ScrollView>(null)

  function handleSelectKind(value: string) {
    setSelectedKind(value)
    if (tabBarRef.current) {
      if (value === 'buyCoin' || value === 'sellCoin' || value === 'return' || value === 'others') {
        tabBarRef.current.scrollTo({ x: 9999, animated: true })
      } else {
        tabBarRef.current.scrollTo({ x: 0, animated: true })
      }
    }
  }

  if (coin !== 'YBB') {
    return <NotFoundScreen />
  }

  return (
    <>
      <Stack.Screen
        options={{
          header: () => <NavigationBar>{marketPresentation.platformToken.displayName}</NavigationBar>
        }}
      />
      <View className={'flex-1 bg-background-secondary'}>
        <AssetDetailList
          coin={coin}
          selectedKind={selectedKind}
          tabBarRef={tabBarRef}
          handleSelectKind={handleSelectKind}
        />
        <BottomActionPanel>
          <Button
            size={'md'}
            className={'flex-1'}
            leftElement={<Deposit className={'size-5 text-white'} />}
            onPress={() => {
              router.push('/(main)/deposit')
            }}
          >
            {t('asset:deposit')}
          </Button>
        </BottomActionPanel>
      </View>
    </>
  )
}

function Overview({ coin }: { coin: string }) {
  const { t } = useTranslation()
  const { data } = useQuery(GET_WALLET_BY_CURRENCY, {
    pollInterval: 5000,
    variables: {
      currencyKind: coin
    },
    // TODO: Handle error
    onError: (error) => {
      console.log('Get Wallet Error:', error)
    }
  })

  const walletDisplay = {
    available: '−.−−',
    frozen: '−.−−',
    total: '−.−−'
  }

  if (data) {
    const wallet = data.getWalletByCurrency
    const available = getBignumber(wallet?.balance.value)
    walletDisplay.available = toCurrency(available)
    const frozen = getBignumber(wallet?.frozenBalance.value).add(getBignumber(wallet?.lockedBalance.value))
    walletDisplay.frozen = toCurrency(frozen)
    const total = available?.add(frozen)
    walletDisplay.total = toCurrency(total)
  }

  return (
    <View className={'gap-4 border-b border-border-10 bg-background-primary px-6 pb-[15px] pt-4'}>
      <View className={'items-center'}>
        <BrandLogo className={'size-10'} />
        <View className={'h-2'} />
        <Text className={'font-roboto-semibold text-2xl text-text-primary'}>{walletDisplay.total}</Text>
        <Text className={'font-roboto text-sm text-text-secondary'}>
          {t('ui:approximate', { amount: formatLocalFiatAmount(walletDisplay.total) })}
        </Text>
      </View>
      <View className={'flex-row gap-2'}>
        <View className={'flex-1 items-center gap-1'}>
          <Text className={'text-xs text-text-secondary'}>{t('asset:availableBalance')}</Text>
          <Text className={'font-roboto-medium text-xs'}>{walletDisplay.available}</Text>
        </View>
        <View className={'flex-1 items-center gap-1'}>
          <Text className={'text-xs text-text-secondary'}>{t('asset:lockedQuantity')}</Text>
          <Text className={'font-roboto-medium text-xs'}>{walletDisplay.frozen}</Text>
        </View>
      </View>
    </View>
  )
}

function AssetDetailTabBar({
  containerRef,
  options,
  selectedKind,
  handleSelect
}: {
  containerRef: React.RefObject<ScrollView | null>
  options: AssetOptions
  selectedKind: string
  handleSelect: (value: string) => void
}) {
  const { t } = useTranslation()
  const { width: windowWidth } = useWindowDimensions()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const needScroll = options.length * 60 > windowWidth

  return (
    <View className={cn(needScroll && 'flex-row')}>
      <ScrollView
        ref={containerRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        className={'bg-background-primary'}
        contentContainerClassName={'flex-row'}
        style={needScroll ? { flex: 1 } : undefined}
      >
        <View className={'absolute inset-x-0 bottom-0 h-px bg-border-10'} />
        {options.map((kind, index) => (
          <Pressable
            key={index}
            className={'relative h-11 w-[60px] items-center justify-center'}
            onPress={() => handleSelect(kind)}
          >
            <Text
              className={cn('text-sm font-medium', selectedKind === kind ? 'text-text-primary' : 'text-text-tertiary')}
            >
              {t(`asset:filter.${kind}`)}
            </Text>
            {selectedKind === kind && (
              <View className={'absolute inset-x-3 bottom-0 mx-auto h-0.5 rounded-t-full bg-background-brand'} />
            )}
          </Pressable>
        ))}
      </ScrollView>
      {needScroll && (
        <Pressable
          className={cn(
            'aspect-square h-11 items-center justify-center border-b border-border-10 bg-background-primary',
            isDrawerOpen ? 'bg-fill-flat-active' : 'bg-white'
          )}
          style={{
            shadowOffset: {
              width: 0,
              height: 2
            },
            shadowColor: basic.gray['100'],
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 8
          }}
          onPress={() => {
            setIsDrawerOpen(true)
            SheetManager.show('wallet-event-filter', {
              payload: {
                value: selectedKind,
                setValue: (kind) => handleSelect(kind),
                closeDrawer: () => setIsDrawerOpen(false)
              }
            })
          }}
        >
          <ExpandMore className={'size-5 text-text-secondary'} />
        </Pressable>
      )}
    </View>
  )
}

function AssetDetailList({
  coin,
  selectedKind,
  tabBarRef,
  handleSelectKind
}: {
  coin: string
  selectedKind: string
  tabBarRef: React.RefObject<ScrollView | null>
  handleSelectKind: (kind: string) => void
}) {
  const { t } = useTranslation()
  const { height: windowHeight } = useWindowDimensions()
  const router = useRouter()
  const { top, bottom } = useSafeAreaInsets()
  const listHeight = Math.max(windowHeight - top - (44 + 184 + 48 + 44 + 72), 68 * 2)

  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isFetchingMore, setIsFetchingMore] = useState(false)

  const { data, fetchMore, refetch, loading } = useQuery(USER_WALLET_EVENTS_CONNECTION, {
    pollInterval: 60000,
    variables: {
      last: PAGE_SIZE,
      currencyKind: coin,
      eventKind: kindToEventKind(selectedKind)
    },
    onError: (error) => console.log(error)
  })

  async function handleFetchMore() {
    const hasPreviousPage = data?.userWalletEventsConnection.pageInfo.hasPreviousPage
    if (hasPreviousPage) {
      setIsFetchingMore(true)
      try {
        await fetchMore({
          variables: {
            last: PAGE_SIZE,
            currencyKind: coin,
            eventKind: kindToEventKind(selectedKind),
            before: data?.userWalletEventsConnection.pageInfo.startCursor
          }
        })
      } finally {
        setIsFetchingMore(false)
      }
    }
  }

  async function handleRefetch() {
    setIsRefreshing(true)
    try {
      await refetch()
    } finally {
      setIsRefreshing(false)
    }
  }

  const walletEvents =
    data?.userWalletEventsConnection.edges.toReversed().map((event: any) => ({ ...event, id: event.cursor })) ?? []

  const isQueryLoading = loading && !isFetchingMore && !isRefreshing
  const events = isQueryLoading ? [] : getEvents(walletEvents)

  return (
    <FlatList
      className={'flex-1'}
      contentContainerStyle={{ flexGrow: 1, paddingBottom: bottom + 72 }}
      data={events}
      keyExtractor={(item) => item.id}
      onEndReached={handleFetchMore}
      refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={handleRefetch} />}
      ListHeaderComponent={
        <>
          <Overview coin={coin} />
          <SectionHeader>{t('asset:assetDetails')}</SectionHeader>
          <AssetDetailTabBar
            containerRef={tabBarRef}
            options={ASSET_OPTIONS}
            selectedKind={selectedKind}
            handleSelect={handleSelectKind}
          />
        </>
      }
      ListEmptyComponent={
        <View style={{ minHeight: listHeight }} className={'items-center justify-center gap-4 bg-background-secondary'}>
          {isQueryLoading ? (
            <View className={'animate-spin'}>
              <Spinner className={'size-6 text-primary'} />
            </View>
          ) : (
            <>
              <ReceiptOff className={'size-20 text-text-quaternary'} />
              <Text className={'text-sm text-text-secondary'}>{t('orders:noOrders')}</Text>
            </>
          )}
        </View>
      }
      ListFooterComponent={
        isFetchingMore ? (
          <View className={'mx-auto my-4 animate-spin'}>
            <Spinner className={'size-6 text-primary'} />
          </View>
        ) : null
      }
      renderItem={({ item: { kind, internalKey, childId, when, amount, eventNumber }, index }) => (
        <Pressable
          className={'bg-background-primary'}
          onPress={() => {
            if (kind === WalletEventKind[WalletEventKind.BuyOrderCompleted]) {
              router.push(`/order/buy/${internalKey}`)
            } else if (kind === WalletEventKind[WalletEventKind.SellOrderCompleted]) {
              router.push(`/order/sell/${internalKey}?childId=${childId}`)
            } else {
              router.push(`/asset/${coin}/${eventNumber}`)
            }
          }}
        >
          {({ pressed }) => {
            const sign = getSign(WalletEventKind[kind as keyof typeof WalletEventKind])
            return (
              <View className={cn(pressed && 'bg-background-secondary')}>
                <View
                  className={cn(
                    'h-[68px] flex-row border-b border-border-10 py-3 pr-4',
                    index === events.length - 1 ? 'pl-4' : 'ml-4'
                  )}
                >
                  <View className={'gap-1'}>
                    <Text className={'text-base text-text-primary'}>{t(`walletEvent:${kind}`)}</Text>
                    <Text className={'text-xs text-text-secondary'}>{convertToDate(new Date(when).valueOf())}</Text>
                  </View>
                  <View className={'flex-1 items-end gap-1'}>
                    <View className={'flex-row items-center gap-2'}>
                      <Text className={cn('font-roboto text-base', sign === '+' ? 'text-success' : 'text-danger')}>
                        {sign}
                        {toCurrency(amount.toString())}
                      </Text>
                      <ChevronRight className={'text-text-tertiary'} />
                    </View>
                  </View>
                </View>
              </View>
            )
          }}
        </Pressable>
      )}
    />
  )
}

const kindToEventKind = (kind: string): WalletEventKind[] => {
  switch (kind) {
    case 'all':
      return [
        WalletEventKind.Transferred,
        WalletEventKind.TransferredWithBonus,
        WalletEventKind.Received,
        WalletEventKind.BuyOrderCompleted,
        WalletEventKind.ExchangedIn,
        WalletEventKind.BonusActivityRewarded,
        WalletEventKind.SellOrderCompleted,
        WalletEventKind.Credited,
        WalletEventKind.DebitCompleted
      ]
    case 'deposit':
      return [WalletEventKind.Received]
    case 'payment':
      return [WalletEventKind.Transferred, WalletEventKind.TransferredWithBonus]
    case 'swap':
      return [WalletEventKind.ExchangedIn]
    case 'buyCoin':
      return [WalletEventKind.BuyOrderCompleted]
    case 'sellCoin':
      return [WalletEventKind.SellOrderCompleted]
    case 'return':
      return [WalletEventKind.BonusActivityRewarded]
    case 'others':
      return [WalletEventKind.Credited, WalletEventKind.DebitCompleted]
    default:
      throw new Error('Invalid kind')
  }
}

// TODO: Consider moving this out
function getSign(kind: WalletEventKind): string {
  switch (kind) {
    case WalletEventKind.Deposited:
    case WalletEventKind.Received:
    case WalletEventKind.BuyOrderCompleted:
    case WalletEventKind.ExchangedIn:
    case WalletEventKind.Refunded:
    case WalletEventKind.SellOrderRefunded:
    case WalletEventKind.SellOrderRebated:
    case WalletEventKind.BonusActivityRewarded:
    case WalletEventKind.Credited:
      return '+'
    case WalletEventKind.Withdrawn:
    case WalletEventKind.Reserved:
    case WalletEventKind.Paid:
    case WalletEventKind.Transferred:
    case WalletEventKind.TransferredWithBonus:
    case WalletEventKind.Debited:
    case WalletEventKind.Locked:
    case WalletEventKind.Frozen:
    case WalletEventKind.FeeCharged:
    case WalletEventKind.SellOrderReserved:
    case WalletEventKind.SellOrderCompleted:
    case WalletEventKind.ExchangedOut:
    case WalletEventKind.DebitCompleted:
      return '−'
    default:
      return ''
  }
}

function getEvents(walletEvents: any[]) {
  return walletEvents.map(({ node: { kind, when, amount, eventNumber, internalKey, metadata }, id }) => ({
    id,
    kind,
    when,
    amount: amount.value,
    eventNumber,
    internalKey,
    childId: metadata?.pairBuyOrderInternalKey ?? '-'
  }))
}
